// Old Dominion University: public college page (#2739), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - ODU's Common Data Set 2025-2026 (Institutional Research, Common Data
//    Set page): C1 applicants, admits and enrollees for Fall 2025 with the
//    residency table, C8 test policy for Fall 2027, C21 (no early decision)
//    and C22 (early action, closing December 1, notification January 15; no
//    early action counts are given). Its G1/G5 costs are for 2025-2026, so
//    the cost comes from the financial aid office instead.
//  - odu.edu Undergraduate Admissions (Freshman Applicants section): the
//    required items, the test policy, the programs that need a second
//    application, and the deadlines table. The deadlines table carries no
//    year; it is the page as posted for the application cycle open now
//    (Fall 2027 entry).
//  - ODU Office of Student Financial Aid, Current Student Costs: the main
//    campus undergraduate budget. The page carries no year label; it was
//    last updated May 11, 2026. Its in-state tuition and fees ($13,656 for
//    30 hours) is above the CDS 2025-2026 tuition plus required fees
//    ($13,320) and fits the Tuition Rates page's rate effective Fall 2026
//    ($443 an hour, $13,290 for 30 hours, before fees), so it is shown as
//    2026-2027. The page's items were not reconciled fee by fee.
//  - College Scorecard API, school id 232982 (net price by family income).
// Quirk: the admissions page says ODU does not consider SAT or ACT scores,
// while the CDS C8 marks the SAT or ACT as required for some applicants. The
// same page asks for scores only from non-traditional freshman applicants
// who finished high school within the past three years; the page states the
// admissions office's policy and the CDS line is shown beside it.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'ODU Common Data Set 2025-2026',
        url: 'https://www.odu.edu/sites/default/files/2026/documents/cds-2025-2026.pdf',
        read: '2026-10-01',
    },
    admissions: {
        label: 'ODU Undergraduate Admissions, Freshman Applicants',
        url: 'https://www.odu.edu/admission/undergraduate',
        read: '2026-10-01',
    },
    costs: {
        label: 'ODU Office of Student Financial Aid, Current Student Costs',
        url: 'https://www.odu.edu/financial-aid/costs',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?232982-Old-Dominion-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'old-dominion-university',
    kbId: 'old_dominion_university',
    name: 'Old Dominion University',
    shortName: 'ODU',
    aliases: ['Old Dominion'],
    location: 'Norfolk, VA',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (ODU identity standards, palette and fonts); decoration only.
    branding: {
        primary: '#003057',
        secondary: '#828A8F',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/old_dominion_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 15024,
        admitted: 13725,
        enrolled: 2697,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 11577, admitted: 10701 },
            { group: 'Out-of-state', applied: 3236, admitted: 2820 },
            { group: 'International', applied: 211, admitted: 204 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'ODU\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'ODU has early action, which is not binding, and no early decision. The Common Data Set gives no early action counts.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Admission to ODU does not guarantee admission to a specific degree program. These programs need a second application once you are admitted to the university:',
        rows: [
            { school: 'Cytotechnology', requirement: 'Separate application after admission to ODU.' },
            { school: 'Dental Hygiene', requirement: 'Separate application after admission to ODU.' },
            { school: 'Environmental Health', requirement: 'Separate application after admission to ODU.' },
            { school: 'Medical Technology', requirement: 'Separate application after admission to ODU.' },
            { school: 'Nuclear Medicine Technology', requirement: 'Separate application after admission to ODU.' },
            { school: 'Nursing', requirement: 'Separate application after admission to ODU.' },
            { school: 'Ophthalmic Technology', requirement: 'Separate application after admission to ODU.' },
        ],
        source: 'admissions',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Early Action', value: 'December 1' },
        rows: [
            { plan: 'Early Action', deadline: 'December 1', decision: 'January 15', binding: false },
            { plan: 'Regular Decision', deadline: 'February 1 (rolling)', decision: 'Rolling', binding: false },
        ],
        note: 'Applications that arrive after these deadlines are reviewed on a rolling basis. The deposit deadline is May 1 for both plans. The application fee is $50, and the only required document is an official high school transcript (or GED transcript); an activity resume, letters of recommendation and a personal statement are optional but strongly recommended. First-year applicants are considered for merit scholarships automatically.',
        source: 'admissions',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-blind',
        blind: true,
        line: 'ODU does not consider SAT or ACT scores for undergraduate admission or standard scholarships. Scores already sent are not considered in the review, but you may send them for class placement or for some endowed scholarships.',
        source: 'admissions',
        cdsLine: 'The Common Data Set marks the SAT or ACT as required for some fall 2027 applicants, with scores due by July 1. On the admissions page, scores are asked only of non-traditional freshman applicants (out of high school more than two years) who finished high school within the past three years.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'admissions',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition and fees', amounts: [13656, 35376] },
            { item: 'Food and housing (on campus)', amounts: [15632, 15632] },
            { item: 'Books and supplies', amounts: [1830, 1830] },
            { item: 'Personal', amounts: [2430, 2430] },
            { item: 'Transportation', amounts: [1380, 1380] },
        ],
        totals: [34928, 56648],
        note: 'The financial aid office\'s estimate for a main campus undergraduate. Tuition and fees assume 30 hours a year; food and housing use the average on-campus housing cost. ODU\'s page names no academic year; it was last updated in May 2026, and its tuition is above the 2025-26 rate, so it is shown as 2026-2027.',
        source: 'costs',
        netPrice: {
            average: 12913,
            byIncome: [
                { income: '$0 to $30,000', amount: 8610 },
                { income: '$30,001 to $48,000', amount: 8652 },
                { income: '$48,001 to $75,000', amount: 9960 },
                { income: '$75,001 to $110,000', amount: 13726 },
                { income: 'Over $110,000', amount: 23199 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from ODU\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
